import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, distinctUntilChanged, fromEvent, map } from 'rxjs';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxIntuitiveTeethDiagramComponent } from '../../components/rx-intuitive-teeth-diagram/rx-intuitive-teeth-diagram.component';
import { RxIntuitiveTeethOrchestratorFacade } from '../../rx-intuitive-teeth-orchestrator.facade';
import { RxIntuitiveIndicationMapperService } from '../../services/rx-intuitive-indication-mapper.service';
import { RxIntuitiveToothEditorFacade } from '../../rx-intuitive-tooth-editor.facade';
import { RxIntuitiveToothEditorBridgeFacade } from '../../rx-intuitive-tooth-editor-bridge.facade';
import { RxIntuitiveBridgeValidationService } from '../../services/rx-intuitive-bridge-validation.service';
import { RxIntuitiveTeethOrchestratorBridgeFacade } from '../../rx-intuitive-teeth-orchestrator-bridge.facade';
import {
	BridgeBannerCloseEvent,
	RxIntuitiveTeethDiagramBridgeBannerComponent
} from '../../components/rx-intuitive-bridge-banner/rx-intuitive-bridge-banner.component';
import { RxIntuitiveBridgeIndicationButtonsComponent } from '../../components/rx-intuitive-bridge-indication-buttons/rx-intuitive-bridge-indication-buttons.component';
import { TlkBannerComponent } from '@itero/ui-toolkit-angular/banner';
import { RxIntuitiveToothEditorBridgeContainerComponent } from '@modules/rx-intuitive-teeth-orchestrator/containers/rx-intuitive-tooth-editor-bridge-container/rx-intuitive-tooth-editor-bridge-container.component';
import { RxIntuitiveBridgeFooterContainerComponent } from '@modules/rx-intuitive-teeth-orchestrator/containers/rx-intuitive-bridge-footer-container/rx-intuitive-bridge-footer-container.component';
import { BridgeStrategy } from '../../services/intuitive-teeth-diagram-button-strategy/intuitive-bridge.strategy';
import { INDICATION_OPERATIONS } from '../../services/intuitive-teeth-diagram-button-strategy/indication-context.token';
import { BridgeIndicationService } from '../../services/intuitive-teeth-diagram-button-strategy/rx-intuitive-indication-opearations/bridge-indication-operations.service';
import { RxIntuitiveTeethOrchestratorLayoutComponent } from '../rx-intuitive-teeth-orchestrator-layout/rx-intuitive-teeth-orchestrator-layout.component';
import { RxIntuitiveTeethHelperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-teeth-helper.service';
import { RxIntuitiveToothEditorRoter } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-tooth-editor-roter.service';
import { RxIntuitiveToothEditorReadOnlyService } from '../../services/tooth-editor/rx-intuitive-tooth-editor-read-only.service';
import { RxIntuitiveToothEditorMutationService } from '../../services/tooth-editor/rx-intuitive-tooth-editor-mutation.service';
import { RxIntuitiveToothEditorViewService } from '../../services/tooth-editor/rx-intuitive-tooth-editor-view.service';
import { RxIntuitiveTeethDiagramMutationService } from '../../services/teeth-diagram/rx-intuitive-teeth-diagram-mutation.service';
import { RxIntuitiveTeethDiagramViewService } from '../../services/teeth-diagram/rx-intuitive-teeth-diagram-view.service';
import { IntuitiveIndication } from '../../models';

@Component({
	selector: 'rx-intuitive-teeth-bridge-orchestrator',
	templateUrl: './rx-intuitive-teeth-bridge-orchestrator.component.html',
	styleUrls: [
		'../rx-intuitive-teeth-orchestrator/rx-intuitive-teeth-orchestrator.component.scss',
		'./rx-intuitive-teeth-bridge-orchestrator.component.scss'
	],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		RxIntuitiveTeethHelperService,
		RxIntuitiveToothEditorRoter,
		RxIntuitiveIndicationMapperService,
		RxIntuitiveBridgeValidationService,
		RxIntuitiveTeethOrchestratorBridgeFacade,
		RxIntuitiveToothEditorFacade,
		RxIntuitiveToothEditorBridgeFacade,
		{ provide: RxIntuitiveTeethOrchestratorFacade, useExisting: RxIntuitiveTeethOrchestratorBridgeFacade },
		// Composition services required by both tooth editor facades
		RxIntuitiveToothEditorReadOnlyService,
		RxIntuitiveToothEditorMutationService,
		RxIntuitiveToothEditorViewService,
		// Composition services required by both teeth diagram facades
		RxIntuitiveTeethDiagramMutationService,
		RxIntuitiveTeethDiagramViewService,
		// Indication strategy wiring for the bridge context
		BridgeIndicationService,
		{ provide: INDICATION_OPERATIONS, useExisting: BridgeIndicationService },
		BridgeStrategy
	],
	imports: [
		CommonModule,
		TranslateModule,
		RxIntuitiveTeethDiagramComponent,
		RxIntuitiveBridgeIndicationButtonsComponent,
		RxIntuitiveBridgeFooterContainerComponent,
		RxIntuitiveTeethDiagramBridgeBannerComponent,
		TlkBannerComponent,
		RxIntuitiveToothEditorBridgeContainerComponent,
		RxIntuitiveTeethOrchestratorLayoutComponent
	]
})
export class RxIntuitiveTeethBridgeOrchestratorComponent extends BaseDestroyableComponent implements OnInit {
	@ViewChild(RxIntuitiveTeethDiagramComponent, { read: ElementRef }) private readonly teethDiagram?: ElementRef<HTMLElement>;

	// ── Observables sourced from the shared facade token ─────────────────────
	readonly upperJaw$ = this.facade.upperJaw$;
	readonly lowerJaw$ = this.facade.lowerJaw$;
	readonly hasSelectedTeeth$ = this.facade.hasSelectedTeeth$;
	readonly selectedTeethIndicationIds$ = this.facade.selectedTeethIndicationIds$;
	readonly enabledIndications$ = this.facade.enabledIndications$;
	readonly isTeethDiagramReadOnly$ = this.facade.isTeethDiagramReadOnly$;

	/** Bridge context shows all visible indications (not the read-only-filtered toolbar subset). */
	readonly indications$ = this.facade.visibleIndications$;

	readonly canRemoveSelectedTooth$ = combineLatest([this.bridgeFacade.canRemoveSelectedTooth$, this.isTeethDiagramReadOnly$]).pipe(
		map(([canRemove, isReadOnly]) => canRemove && !isReadOnly),
		distinctUntilChanged()
	);

	readonly indicationsDisabled$ = combineLatest([this.hasSelectedTeeth$, this.isTeethDiagramReadOnly$]).pipe(
		map(([hasSelectedTeeth, isReadOnly]) => !hasSelectedTeeth || isReadOnly),
		distinctUntilChanged()
	);

	readonly indicationButtonsAsLabel$ = this.isTeethDiagramReadOnly$.pipe(map(readOnly => readOnly));

	/** In readonly mode: show only the selected tooth's indication (falls back to all if none selected).
	 *  In edit mode: show all visible indications. */
	readonly displayedIndications$ = combineLatest([
		this.facade.visibleIndications$,
		this.facade.selectedTeethIndicationIds$,
		this.isTeethDiagramReadOnly$
	]).pipe(
		map(([allIndications, selectedIndications, isReadOnly]) => {
			if (isReadOnly) {
				return selectedIndications.length > 0 ? selectedIndications : allIndications;
			}

			return allIndications;
		})
	);

	readonly showGuidanceBanner$ = this.bridgeFacade.showGuidanceBanner$;
	readonly validationErrorSummary$ = this.bridgeFacade.validationErrorSummary$;

	private readonly pointerUp$ = fromEvent<PointerEvent>(document, 'pointerup');

	constructor(
		private readonly facade: RxIntuitiveTeethOrchestratorFacade,
		private readonly bridgeFacade: RxIntuitiveTeethOrchestratorBridgeFacade,
		private readonly bridgeStrategy: BridgeStrategy
	) {
		super();
	}

	ngOnInit(): void {
		this.pointerUp$
			.pipe(
				takeUntil(this.componentAlive$),
				withLatestFrom(this.facade.hasSelectedTeeth$),
				filter(([event, hasSelectedTeeth]) => hasSelectedTeeth && !this.isInsideTeethEditingArea(event.target))
			)
			.subscribe(() => this.facade.clearToothSelection());
	}

	onToothClicked(toothId: number): void {
		this.isTeethDiagramReadOnly$.pipe(take(1)).subscribe(isReadOnly => {
			if (isReadOnly) {
				this.bridgeFacade.selectToothInBridgeReadOnly(toothId);
			} else {
				this.facade.toggleToothSelection(toothId);
			}
		});
	}

	onResetSelectedRequested(): void {
		this.facade.clearToothSelection();
	}

	onIndicationSelected(indicationId: IntuitiveIndication): void {
		this.bridgeStrategy.teethDiagramButtonHandle(indicationId).pipe(takeUntil(this.componentAlive$)).subscribe();
	}

	focusDiagram(): void {
		this.teethDiagram?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
		this.teethDiagram?.nativeElement?.focus({ preventScroll: true });
	}

	onRemoveToothFromBridge(): void {
		this.bridgeFacade.removeToothFromBridge();
	}

	onBannerClosed(event: BridgeBannerCloseEvent): void {
		this.bridgeFacade.dismissGuidanceBanner(event.dontShowAgain);
	}

	private isInsideTeethEditingArea(target: EventTarget | null): boolean {
		const editingAreaComponentSelectors = [
			'.cdk-overlay-container',
			'rx-intuitive-implants-view-modal',
			'rx-copy-from-tooth-menu',
			'rx-intuitive-tooth-editor',
			'rx-intuitive-bridge-editor',
			'rx-intuitive-teeth-diagram',
			'rx-intuitive-teeth-treatment-viewlist',
			'rx-intuitive-indication-button',
			'rx-intuitive-remove-from-bridge-button',
			'[rx-intuitive-bridge-indication-buttons] button',
			'[rx-intuitive-teeth-diagram-footer] button',
			'[rx-intuitive-teeth-diagram-banner]'
		];

		if (target == null) {
			return false;
		}

		let element: Element;

		if (target instanceof Element) {
			element = target;
		} else if (target instanceof Node && target.parentElement != null) {
			element = target.parentElement;
		} else {
			return false;
		}

		return editingAreaComponentSelectors.some(s => element.closest(s) != null);
	}
}
